/**
 * Living KPI catalog — pure formatting + threshold logic (framework-free).
 *
 * This is the canonical implementation. The operations-manager plugin ships a
 * byte-identical mirror at `plugins/operations-manager/1.0.0/src/tabs/stats/kpiFormat.ts`
 * because the shell (Vite) and the plugin (esbuild, `React.createElement`) are
 * independent bundle targets that cannot share a module without build wiring —
 * the same reason `usePrefersReducedMotion` is redefined per plugin. Keep the two
 * in sync; there are no side effects or imports here, so a copy is safe.
 */

export type KpiFormat =
  | 'percent'
  | 'duration'
  | 'rate'
  | 'integer'
  | 'number'
  | 'currency'
  | 'state';

export type KpiStatus = 'ok' | 'warn' | 'critical' | 'none';

export interface KpiThresholds {
  warn?: number | null;
  critical?: number | null;
}

export interface KpiMeta {
  id: string;
  domain: string;
  title: string;
  description: string;
  unit: string;
  format: KpiFormat;
  requiredCapability: string;
  grafanaUrl: string;
  /** true => a lower value is worse (availability, hit ratios); default false. */
  higherIsBetter: boolean;
  thresholds: KpiThresholds;
  hasQuery: boolean;
}

export interface KpiCatalog {
  version: number;
  domains: string[];
  kpis: KpiMeta[];
}

/** A single sparkline point from the snapshot endpoint. */
export interface KpiPoint {
  t: number;
  v: number;
}

export type KpiSeries = Record<string, KpiPoint[]>;

// ---------------------------------------------------------------------------
// Domain presentation
// ---------------------------------------------------------------------------

export interface DomainMeta {
  key: string;
  label: string;
  /** Short blurb shown under the domain heading. */
  blurb: string;
}

/** Preferred display order; unknown domains sort after these, alphabetically. */
export const DOMAIN_META: Record<string, DomainMeta> = {
  platform: { key: 'platform', label: 'Platform', blurb: 'Edge, gateway & runtime health' },
  pdp: { key: 'pdp', label: 'Authorization', blurb: 'Policy decisions & cache' },
  idp: { key: 'idp', label: 'Identity', blurb: 'Tokens, sign-in & federation' },
  iga: { key: 'iga', label: 'Governance', blurb: 'Fabric, reconciliation & fulfillment' },
  mcp: { key: 'mcp', label: 'Agents & MCP', blurb: 'Tool routing & agent activity' },
  crud: { key: 'crud', label: 'Orchestration', blurb: 'Workflows & directives' },
};

const DOMAIN_ORDER = ['platform', 'pdp', 'idp', 'iga', 'mcp', 'crud'];

export function domainLabel(domain: string): string {
  return DOMAIN_META[domain]?.label ?? titleCase(domain);
}

export function domainBlurb(domain: string): string {
  return DOMAIN_META[domain]?.blurb ?? '';
}

/** Stable domain sort: known domains by curated order, then unknowns A→Z. */
export function sortDomains(domains: string[]): string[] {
  return [...domains].sort((a, b) => {
    const ia = DOMAIN_ORDER.indexOf(a);
    const ib = DOMAIN_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
}

function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// ---------------------------------------------------------------------------
// Value formatting
// ---------------------------------------------------------------------------

const EMPTY = '\u2014'; // em dash

function formatNumber(v: number, maxFrac = 1): string {
  if (!isFinite(v)) return EMPTY;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${trim(v / 1_000_000, maxFrac)}M`;
  if (abs >= 10_000) return `${trim(v / 1_000, maxFrac)}k`;
  return trim(v, abs < 1 && abs > 0 ? 2 : maxFrac);
}

function trim(v: number, maxFrac: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: maxFrac });
}

/**
 * Human-readable value for display. `value` is the RAW metric value (ratios are
 * 0..1, durations are seconds) — thresholds are evaluated in the same raw space.
 */
export function formatKpiValue(
  value: number | null | undefined,
  format: KpiFormat,
  unit = '',
): string {
  if (value == null || !isFinite(value)) return EMPTY;

  switch (format) {
    case 'percent': {
      // Queries emit ratios (0..1); tolerate already-scaled values > 1.5.
      const pct = value <= 1.5 ? value * 100 : value;
      return `${trim(pct, pct < 10 ? 1 : 0)}%`;
    }
    case 'duration': {
      // seconds → ms / s / m
      if (value < 1) return `${Math.round(value * 1000)}ms`;
      if (value < 60) return `${trim(value, value < 10 ? 2 : 1)}s`;
      const m = Math.floor(value / 60);
      const s = Math.round(value % 60);
      return `${m}m ${s}s`;
    }
    case 'rate': {
      const suffix = unit && unit !== 'count' ? ` ${unit}` : '/s';
      return `${formatNumber(value, 2)}${suffix}`;
    }
    case 'integer':
      return Math.round(value).toLocaleString();
    case 'currency':
      return `$${formatNumber(value, 2)}`;
    case 'state':
      return value >= 1 ? 'Up' : value <= 0 ? 'Down' : formatNumber(value);
    case 'number':
    default:
      return formatNumber(value);
  }
}

// ---------------------------------------------------------------------------
// Threshold evaluation
// ---------------------------------------------------------------------------

/**
 * Classify a raw value against the KPI's thresholds.
 *
 * `higherIsBetter` (registry `direction: down_bad`) flips the comparison so a
 * value at/below the threshold is the bad one. Missing thresholds → `none`.
 */
export function evaluateStatus(
  value: number | null | undefined,
  meta: Pick<KpiMeta, 'thresholds' | 'higherIsBetter'>,
): KpiStatus {
  if (value == null || !isFinite(value)) return 'none';
  const { warn, critical } = meta.thresholds || {};
  const hasWarn = typeof warn === 'number';
  const hasCrit = typeof critical === 'number';
  if (!hasWarn && !hasCrit) return 'none';

  if (meta.higherIsBetter) {
    if (hasCrit && value <= (critical as number)) return 'critical';
    if (hasWarn && value <= (warn as number)) return 'warn';
    return 'ok';
  }
  if (hasCrit && value >= (critical as number)) return 'critical';
  if (hasWarn && value >= (warn as number)) return 'warn';
  return 'ok';
}

/** CSS custom-property expression for a status color (token-compliant). */
export function statusColorVar(status: KpiStatus): string {
  switch (status) {
    case 'critical':
      return 'var(--error-color, #ef4444)';
    case 'warn':
      return 'var(--warning-color, #f7630c)';
    case 'ok':
      return 'var(--success-color, #10b981)';
    case 'none':
    default:
      return 'var(--main-accent, #6C4CFF)';
  }
}

/** Human labels for each status (shared by cards, pills, legend). */
export const STATUS_LABEL: Record<KpiStatus, string> = {
  ok: 'Healthy',
  warn: 'Warning',
  critical: 'Critical',
  none: 'Nominal',
};

export interface ThresholdBand {
  status: Exclude<KpiStatus, 'none'>;
  label: string;
  /** Human range for this band, e.g. "2% – 5%" or "≥ 5%". */
  range: string;
}

/**
 * Build a human-readable threshold legend for the detail popover, consistent
 * with `evaluateStatus` semantics. Returns `[]` when the KPI has no thresholds.
 */
export function thresholdLegend(
  meta: Pick<KpiMeta, 'thresholds' | 'higherIsBetter' | 'format' | 'unit'>,
): ThresholdBand[] {
  const warn = typeof meta.thresholds?.warn === 'number' ? meta.thresholds.warn : null;
  const crit = typeof meta.thresholds?.critical === 'number' ? meta.thresholds.critical : null;
  if (warn == null && crit == null) return [];

  const fmt = (v: number) => formatKpiValue(v, meta.format, meta.unit);
  const bands: ThresholdBand[] = [];

  if (meta.higherIsBetter) {
    // Lower is worse: ok > warn ; warn in (crit, warn] ; critical ≤ crit
    bands.push({ status: 'ok', label: STATUS_LABEL.ok, range: `> ${fmt((warn ?? crit) as number)}` });
    if (warn != null && crit != null) {
      bands.push({ status: 'warn', label: STATUS_LABEL.warn, range: `${fmt(crit)} – ${fmt(warn)}` });
    } else if (warn != null) {
      bands.push({ status: 'warn', label: STATUS_LABEL.warn, range: `≤ ${fmt(warn)}` });
    }
    if (crit != null) bands.push({ status: 'critical', label: STATUS_LABEL.critical, range: `≤ ${fmt(crit)}` });
  } else {
    // Higher is worse (default): ok < warn ; warn in [warn, crit) ; critical ≥ crit
    bands.push({ status: 'ok', label: STATUS_LABEL.ok, range: `< ${fmt((warn ?? crit) as number)}` });
    if (warn != null && crit != null) {
      bands.push({ status: 'warn', label: STATUS_LABEL.warn, range: `${fmt(warn)} – ${fmt(crit)}` });
    } else if (warn != null) {
      bands.push({ status: 'warn', label: STATUS_LABEL.warn, range: `≥ ${fmt(warn)}` });
    }
    if (crit != null) bands.push({ status: 'critical', label: STATUS_LABEL.critical, range: `≥ ${fmt(crit)}` });
  }
  return bands;
}

/**
 * Pure drag-to-reorder: move `dragId` to the slot of `targetId`.
 * `placeBefore` inserts before the target (default), else after. Returns a new
 * array; unknown ids or a no-op move return the original order unchanged.
 */
export function reorderByIds(
  ids: string[],
  dragId: string,
  targetId: string,
  placeBefore = true,
): string[] {
  if (dragId === targetId) return ids;
  const from = ids.indexOf(dragId);
  const target = ids.indexOf(targetId);
  if (from === -1 || target === -1) return ids;
  const without = ids.filter((x) => x !== dragId);
  let insertAt = without.indexOf(targetId);
  if (insertAt === -1) return ids;
  if (!placeBefore) insertAt += 1;
  without.splice(insertAt, 0, dragId);
  return without;
}

// ---------------------------------------------------------------------------
// Series helpers
// ---------------------------------------------------------------------------

/** Latest value from a snapshot series (last point). */
export function latestValue(points: KpiPoint[] | undefined): number | null {
  if (!points || points.length === 0) return null;
  const last = points[points.length - 1];
  return last && isFinite(last.v) ? last.v : null;
}

/** Numeric trend direction comparing first vs last point of the window. */
export function seriesTrend(points: KpiPoint[] | undefined): 'up' | 'down' | 'flat' {
  if (!points || points.length < 2) return 'flat';
  const first = points[0].v;
  const last = points[points.length - 1].v;
  if (!isFinite(first) || !isFinite(last)) return 'flat';
  const delta = last - first;
  const denom = Math.abs(first) || 1;
  if (Math.abs(delta) / denom < 0.02) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

/** Plain numeric values for a sparkline. */
export function seriesValues(points: KpiPoint[] | undefined): number[] {
  return (points || []).map((p) => p.v).filter((v) => isFinite(v));
}

// ---------------------------------------------------------------------------
// Capability filtering (visibility only; real data stays session-gated)
// ---------------------------------------------------------------------------

/**
 * Filter the catalog by the subject's capability tags.
 *
 * Fail-open when `tags` is empty (projection degraded or metric tags not
 * provisioned) — these are read-only operational metrics and the snapshot data
 * endpoint is still session-authenticated. When tags exist, a KPI is visible if
 * it declares no requiredCapability or the tag is present.
 */
export function filterByCapability(kpis: KpiMeta[], tags: string[] | null | undefined): KpiMeta[] {
  if (!tags || tags.length === 0) return kpis;
  const set = new Set(tags);
  return kpis.filter((k) => !k.requiredCapability || set.has(k.requiredCapability));
}
